import { NextResponse } from 'next/server'
import { type CreateInvoiceOrderDTO, type ShipmentDTO, type UpdateInvoiceOrderDTO } from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { transformOrderItemsToCreateOrderItems } from 'api/utils/orders'
import { createLogger } from 'shared-utils'
import {
  buildShipmentFromNshift,
  buildShipmentFromPostnord,
  stripRootShipping,
  withShipment
} from '../shippingProviders'
import { withInvoiceGuards } from './withInvoiceGuards'
import { type InvoiceOrderHandlerOpts } from './types'

interface UpdateInvoiceOrderRequest {
  orderNumber: string
  /** Partial fields the FE wants to merge into the persisted DRAFT.
   *  Shipping intent must be supplied via `overrides.shipments[0]` —
   *  root-level shipping fields are rejected by the BE on POST/PUT. */
  overrides?: Partial<CreateInvoiceOrderDTO>
}
const logger = createLogger('updateInvoiceOrderHandler')
/**
 * Expand a minimal FE-supplied `shipments[0]` into the canonical `ShipmentDTO`
 * shape the BE expects, using the same per-provider builders that the submit
 * handler uses. The FE sends only the provider, shippingType, and provider
 * details in `additionalInfo`; this fleshes out `carrierName`,
 * `carrierService`, `locationId`, `locationName`, `address`, etc.
 *
 * Returns `undefined` when the shipment can't be expanded (no provider,
 * unknown provider, or missing provider details) — the caller leaves the
 * shipment as-is in that case.
 */
const expandShipmentFromProviderDetails = (
  shipment: ShipmentDTO | undefined
): { shipment: ShipmentDTO; shippingCost?: number } | undefined => {
  if (!shipment) return undefined
  switch (shipment.shippingProvider) {
    case 'POSTNORD':
      return buildShipmentFromPostnord(shipment.additionalInfo?.postnordDetails)
    case 'NSHIFT':
      return buildShipmentFromNshift(shipment.additionalInfo?.nshiftDetails)
    default:
      return undefined
  }
}

/**
 * POST /api/orders/invoice-update — generic DRAFT update for an existing
 * invoice order.
 *
 * The FE sends `{ orderNumber, overrides }`. The handler:
 * 1. GETs the persisted DRAFT with `includeItems` (BE replaces — not merges —
 *    on PUT, so omitting `items` would erase them; the merged payload below
 *    has to round-trip them).
 * 2. Merges in the FE-supplied overrides.
 * 3. Expands `shipments[0]` from the FE-supplied provider details into the
 *    canonical `ShipmentDTO` shape (carrierName, locationId, address, …) so
 *    every DRAFT update PUTs the full shipment, not just provider + details.
 * 4. Strips the root-level shipping fields (the BE rejects them per
 *    FEATURE_B2B_ORDER_PLACEMENT.md §7) and PUTs.
 *
 * `customerId` is overwritten from the iron-session so the FE can't spoof it.
 */
export const updateInvoiceOrderRouteHandler = (opts: InvoiceOrderHandlerOpts) =>
  withInvoiceGuards(opts, async (request) => {
    const { orderNumber, overrides } = (await request.json()) as UpdateInvoiceOrderRequest

    if (!orderNumber) {
      return NextResponse.json(
        { error: 'orderNumber is required to update an invoice order.' },
        { status: 400 }
      )
    }

    const { data: existing, error: getErr } = await storefrontApi.invoiceOrders.getInvoiceOrder({
      orderNumber,
      includeItems: true,
      includeCustomer: true
    })

    if (getErr || !existing) {
      return NextResponse.json(
        { error: typeof getErr === 'string' ? getErr : 'invoice-order-not-found' },
        { status: 404 }
      )
    }

    const merged: Record<string, unknown> = {
      ...existing,
      orderNumber: existing.orderNumber ?? orderNumber,
      customerId: opts.customerId,
      items: existing.items ? transformOrderItemsToCreateOrderItems(existing.items) : [],
      ...(existing.billingAddress ? { billingAddress: existing.billingAddress } : {}),
      ...overrides
    }

    const incomingShipment = (merged.shipments as ShipmentDTO[] | undefined)?.[0]
    const built = expandShipmentFromProviderDetails(incomingShipment)
    const withCanonicalShipment = built
      ? (withShipment(merged, built) as Record<string, unknown>)
      : merged

    const payload = stripRootShipping(withCanonicalShipment) as unknown as UpdateInvoiceOrderDTO

    logger.log('BE REQ PAYLOAD', payload)

    return NextResponse.json(await storefrontApi.invoiceOrders.updateInvoiceOrder(payload))
  })
